import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Heart, Shield, Sparkles, AlertCircle } from 'lucide-react';
import { SectionWrapper } from '../components/SectionWrapper';
import { EditorialButton } from '../components/EditorialButton';
import { trackEvent } from '../hooks/useMetaPixel';
export function LandingPage() {
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#FAF6F1] text-[#3D2B1F] font-sans overflow-x-hidden selection:bg-[#E5D0C0] selection:text-[#3D2B1F]" data-id="element-4">
      {/* HERO SECTION */}
      <header className="relative w-full pt-12 pb-20 md:pt-20 md:pb-32 px-6" data-id="element-5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-6">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="order-2 md:order-1" data-id="element-7">
            <span className="block text-[#B87333] font-serif italic text-xl mb-4 tracking-wide" data-id="element-8">
              Guía de seguridad después del divorcio
            </span>
            <h1 className="text-5xl md:text-7xl font-serif font-medium leading-[1.1] mb-6 text-[#3D2B1F]" data-id="element-9">
              Volví a <br data-id="element-10" />
              <span className="italic text-[#B87333]" data-id="element-11">mi cuerpo</span>
            </h1>
            <p className="text-xl md:text-2xl text-[#4A3728] mb-8 font-light leading-relaxed" data-id="element-12">
              Sexualidad después del divorcio <br data-id="element-13" />
              <span className="italic opacity-80" data-id="element-14">(sin culpa, sin prisa)</span>
            </p>

            <div className="space-y-6 text-lg text-[#4A3728] leading-relaxed mb-10" data-id="element-15">
              <p data-id="element-16">
                Hay un miedo que casi nadie dice en voz alta después del
                divorcio: que tu primera vez "de regreso" no sea liberadora…
                sino confusa.
              </p>
              <p data-id="element-17">
                Que te rías por fuera, pero por dentro estés contando segundos.
                Que digas que sí… y luego, sola, te invada esa sensación amarga
                de: "¿Por qué hice eso?"
              </p>
              <p data-id="element-18">
                Esta guía existe para una tercera opción: volver a ti primero.
                Sin culpa. Sin prisa. Sin actuar desde vacío.
              </p>
            </div>

            <EditorialButton onClick={scrollToOffer} data-id="element-19">
              Quiero la guía <ArrowRight className="ml-2 h-5 w-5" data-id="element-20" />
            </EditorialButton>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          ease: 'easeOut',
          delay: 0.2
        }} className="order-1 md:order-2 relative" data-id="element-21">
            <div className="relative rounded-t-[10rem] rounded-b-lg overflow-hidden shadow-2xl aspect-[3/4] md:aspect-[4/5]" data-id="element-22">
              <img src="https://cdn.magicpatterns.com/uploads/wjT1uX8zna4HR4bz3J3eh2/private_openrouter-generated-image-1770742105454.png" alt="Mujer mirando al horizonte con serenidad" className="w-full h-full object-cover" data-id="element-23" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3D2B1F]/20 to-transparent mix-blend-multiply pointer-events-none" data-id="element-24"></div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* PARA QUIÉN ES */}
      <SectionWrapper className="text-center border-y border-[#E5D0C0]" data-id="element-25">
        <div className="max-w-2xl mx-auto py-8" data-id="element-26">
          <span className="block w-12 h-px bg-[#B87333] mx-auto mb-8" data-id="element-27"></span>
          <p className="text-2xl md:text-3xl font-serif italic text-[#4A3728] leading-relaxed" data-id="element-28">
            "Para la mujer que quiere volver a su vida íntima, pero no quiere
            pagar el precio de traicionarse."
          </p>
          <span className="block w-12 h-px bg-[#B87333] mx-auto mt-8" data-id="element-29"></span>
        </div>
      </SectionWrapper>

      {/* PAIN POINTS */}
      <SectionWrapper className="bg-[#FAF6F1]" data-id="element-30">
        <h2 className="text-3xl md:text-4xl font-serif text-center mb-4" data-id="element-31">
          Lo que probablemente te está pasando
        </h2>
        <p className="text-center text-[#4A3728]/80 mb-12 italic" data-id="element-32">
          (aunque no lo llames así)
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-33">
          {['Te atrae alguien… pero tu cuerpo se cierra.', "Te arreglas… y aun así te sientes 'rara', como si el divorcio se notara en la piel.", "Dices 'sí' por evitar incomodidad, y luego te quedas con esa resaca emocional.", "Cierras todo y te convences de que 'no te importa'.", 'En tu cabeza suena como un defecto personal.'].map((point, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 bg-[#F5EDE4] rounded-sm border border-[#E5D0C0] ${i === 4 ? 'md:col-span-2 md:w-2/3 md:mx-auto' : ''}`} data-id="element-34">
              <div className="flex items-start gap-4" data-id="element-35">
                <AlertCircle className="w-6 h-6 text-[#B87333] flex-shrink-0 mt-1" data-id="element-36" />
                <p className="text-lg text-[#4A3728]" data-id="element-37">{point}</p>
              </div>
            </motion.div>)}
        </div>

        <div className="mt-16 text-center max-w-2xl mx-auto" data-id="element-38">
          <p className="text-xl font-medium mb-4" data-id="element-39">No es un defecto personal.</p>
          <p className="text-lg text-[#4A3728] leading-relaxed" data-id="element-40">
            La mayoría de las veces, no es falta de deseo.{' '}
            <span className="font-semibold text-[#B87333]" data-id="element-41">Es alerta.</span>{' '}
            <br data-id="element-42" />
            Tu cuerpo aprendió que la intimidad puede traer presión, culpa o
            dolor. Y ahora está tratando de protegerte… aunque lo haga de formas
            torpes.
          </p>
        </div>
      </SectionWrapper>

      {/* THE PROMISE */}
      <div className="bg-[#F5EDE4] py-20" data-id="element-43">
        <SectionWrapper data-id="element-44">
          <div className="text-center mb-12" data-id="element-45">
            <h2 className="text-3xl md:text-4xl font-serif mb-6" data-id="element-46">
              La promesa{' '}
              <span className="italic opacity-70 text-2xl block mt-2" data-id="element-47">
                (sin fantasía)
              </span>
            </h2>
            <p className="text-xl text-[#4A3728]" data-id="element-48">
              En 14 días vas a tener un sistema claro para:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-49">
            {[{
            title: 'Bajar la ansiedad',
            desc: 'antes de decidir'
          }, {
            title: 'Reconectar con placer',
            desc: 'sin prisa'
          }, {
            title: 'Elegir desde poder',
            desc: 'con límites que no apagan la química'
          }].map((item, i) => <motion.div key={i} className="text-center p-6" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.15
          }} data-id="element-50">
                <div className="w-12 h-12 bg-[#B87333]/10 rounded-full flex items-center justify-center mx-auto mb-4 text-[#B87333]" data-id="element-51">
                  <Sparkles className="w-6 h-6" data-id="element-52" />
                </div>
                <h3 className="text-xl font-serif font-medium mb-2" data-id="element-53">
                  {item.title}
                </h3>
                <p className="text-[#4A3728]/80 italic" data-id="element-54">{item.desc}</p>
              </motion.div>)}
          </div>

          <p className="text-center text-xl font-serif italic text-[#B87333]" data-id="element-55">
            "No es 'sé una nueva tú'. Es: vuelve a tu cuerpo y deja de
            empujarte."
          </p>
        </SectionWrapper>
      </div>

      {/* WHY OLD SOLUTIONS FAIL */}
      <SectionWrapper data-id="element-56">
        <h2 className="text-3xl md:text-4xl font-serif text-center mb-12" data-id="element-57">
          Por qué lo que te dijeron no funciona
        </h2>

        <div className="space-y-8 max-w-3xl mx-auto" data-id="element-58">
          {[{
          title: '1) "Solo sal con alguien y ya"',
          text: 'Eso funciona para quien no se activa. Si tú te activas, eso te mete a una experiencia donde tu mente va por un lado y tu cuerpo por otro.'
        }, {
          title: '2) "Mejor no hagas nada por mucho tiempo"',
          text: 'A veces es descanso. Pero muchas veces es miedo con buena prensa.'
        }, {
          title: '3) "Aprende más sobre sexo"',
          text: 'La técnica sin seguridad interna no cura la vergüenza. Solo la tapa.'
        }].map((item, i) => <motion.div key={i} className="flex gap-6 items-start border-l-2 border-[#E5D0C0] pl-6 py-2" whileHover={{
          x: 5
        }} data-id="element-59">
              <div data-id="element-60">
                <h3 className="text-xl font-serif font-semibold text-[#3D2B1F] mb-2" data-id="element-61">
                  {item.title}
                </h3>
                <p className="text-[#4A3728] leading-relaxed" data-id="element-62">{item.text}</p>
              </div>
            </motion.div>)}
        </div>
      </SectionWrapper>

      {/* THE SOLUTION: PROTOCOLO 3R */}
      <div className="bg-[#3D2B1F] text-[#FAF6F1] py-24" data-id="element-63">
        <SectionWrapper data-id="element-64">
          <div className="text-center mb-16" data-id="element-65">
            <span className="text-[#B87333] tracking-widest uppercase text-sm font-semibold mb-4 block" data-id="element-66">
              La Solución
            </span>
            <h2 className="text-4xl md:text-5xl font-serif mb-6" data-id="element-67">
              Protocolo 3R
            </h2>
            <p className="text-[#FAF6F1]/80 max-w-2xl mx-auto text-lg" data-id="element-68">
              Un sistema paso a paso para recuperar tu seguridad íntima.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-69">
            {[{
            step: '01',
            name: 'REGULAR',
            desc: "Primero bajas la alerta del cuerpo. Sin eso, cualquier 'sí' se vuelve frágil."
          }, {
            step: '02',
            name: 'RECONECTAR',
            desc: 'Luego vuelves a sentir. Placer mínimo viable. Deseo sin urgencia. Claridad sin castigo.'
          }, {
            step: '03',
            name: 'RE-ELEGIR',
            desc: 'Por último, eliges personas, ritmos y situaciones desde tu criterio… no desde tu vacío.'
          }].map((r, i) => <motion.div key={i} className="bg-[#4A3728] p-8 rounded-sm border border-[#5C4838]" whileHover={{
            y: -5
          }} data-id="element-70">
                <span className="text-6xl font-serif text-[#B87333]/20 font-bold block mb-4" data-id="element-71">
                  {r.step}
                </span>
                <h3 className="text-2xl font-serif text-[#FAF6F1] mb-4" data-id="element-72">
                  {r.name}
                </h3>
                <p className="text-[#FAF6F1]/80 leading-relaxed" data-id="element-73">{r.desc}</p>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center" data-id="element-74">
            <p className="text-xl text-[#FAF6F1]/90 mb-8" data-id="element-75">
              Y para tomar decisiones rápidas sin enredarte: <br data-id="element-76" />
              <span className="text-[#B87333] font-serif italic text-2xl mt-2 block" data-id="element-77">
                Escala Verde / Amarilla / Roja
              </span>
            </p>
          </div>
        </SectionWrapper>
      </div>

      {/* WHAT'S INCLUDED */}
      <SectionWrapper className="py-20" data-id="element-78">
        <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-79">
          <div className="flex-1" data-id="element-80">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-81">
              Lo que recibes
            </h2>
            <ul className="space-y-6" data-id="element-82">
              {['Ejercicios simples de regulación (sin espiritualidad obligatoria)', '“Deseo vs soledad”: prueba rápida antes de decir sí', 'Guiones listos para límites (Deseo + Límite + Puerta)', 'Plan 24/24: 24 horas antes y 24 después de tu primera intimidad', 'Workbook imprimible: no negociables, escala V/A/R, aftercare personal'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-83">
                  <Check className="w-5 h-5 text-[#B87333] mt-1 flex-shrink-0" data-id="element-84" />
                  <span className="text-lg text-[#4A3728]" data-id="element-85">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="flex-1 bg-[#F5EDE4] p-8 md:p-12 rounded-sm border border-[#E5D0C0] rotate-1 shadow-lg cursor-pointer transition-shadow hover:shadow-xl" onClick={scrollToOffer} data-id="element-86">
            <div className="text-center border-2 border-[#B87333] p-8 h-full flex flex-col justify-center items-center" data-id="element-87">
              <span className="font-serif text-2xl text-[#3D2B1F] mb-2" data-id="element-88">
                Workbook & Guía
              </span>
              <span className="text-5xl font-serif text-[#B87333] mb-4" data-id="element-89">
                14 Días
              </span>
              <p className="text-[#4A3728] italic" data-id="element-90">
                PDF descargable + Hojas de trabajo
              </p>
              <span className="mt-4 inline-flex items-center text-[#B87333] font-medium text-sm tracking-wide uppercase" data-id="element-91">
                Descargar ahora <ArrowRight className="ml-2 h-4 w-4" data-id="element-92" />
              </span>
            </div>
          </div>
        </div>
      </SectionWrapper>

      {/* BENEFITS */}
      <div className="bg-[#F5EDE4] py-20" data-id="element-93">
        <SectionWrapper data-id="element-94">
          <h2 className="text-3xl md:text-4xl font-serif text-center mb-12" data-id="element-95">
            Qué cambia cuando haces esto bien
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-96">
            {["No es que 'te vuelves más sexual'. Es que te vuelves más tuya.", 'Puedes pausar sin sentir culpa.', "Puedes decir 'no' sin explicar tu biografía.", 'Puedes disfrutar sin pagar con vergüenza después.'].map((benefit, i) => <motion.div key={i} className="bg-[#FAF6F1] p-6 rounded-sm shadow-sm flex items-center gap-4" initial={{
            opacity: 0,
            x: i % 2 === 0 ? -20 : 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} data-id="element-97">
                <Heart className="w-5 h-5 text-[#B87333]" data-id="element-98" />
                <span className="text-lg text-[#3D2B1F] font-medium" data-id="element-99">
                  {benefit}
                </span>
              </motion.div>)}
          </div>

          <p className="text-center mt-12 text-2xl font-serif italic text-[#B87333]" data-id="element-100">
            "Ese es el verdadero upgrade."
          </p>
        </SectionWrapper>
      </div>

      {/* OBJECTIONS */}
      <SectionWrapper data-id="element-101">
        <h2 className="text-3xl md:text-4xl font-serif text-center mb-12" data-id="element-102">
          Preguntas frecuentes (y miedos reales)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-103">
          {[{
          q: '¿Y si todavía no estoy lista?',
          a: 'Perfecto. Esta guía empieza ahí. No te empuja: te ordena.'
        }, {
          q: 'Me da vergüenza leer esto.',
          a: 'Esa vergüenza es exactamente el punto de entrada. Aquí se trabaja sin humillarte.'
        }, {
          q: '¿Y si lloro o me siento rara?',
          a: 'Entonces estás procesando. Por eso existe el aftercare y la integración.'
        }].map((obj, i) => <div key={i} className="bg-white p-8 border border-[#E5D0C0] rounded-sm" data-id="element-104">
              <h3 className="font-serif text-xl font-bold text-[#3D2B1F] mb-4" data-id="element-105">
                "{obj.q}"
              </h3>
              <p className="text-[#4A3728] leading-relaxed" data-id="element-106">{obj.a}</p>
            </div>)}
        </div>
      </SectionWrapper>

      {/* OFFER SECTION */}
      <div id="offer" className="bg-[#3D2B1F] text-[#FAF6F1] py-24 relative overflow-hidden" data-id="element-107">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-108">
          <div className="absolute top-[-50%] left-[-20%] w-[800px] h-[800px] rounded-full bg-[#B87333] blur-[120px]" data-id="element-109"></div>
        </div>

        <SectionWrapper className="relative z-10 text-center" data-id="element-110">
          <h2 className="text-4xl md:text-6xl font-serif mb-6" data-id="element-111">
            Volví a mi cuerpo
          </h2>
          <p className="text-xl md:text-2xl text-[#FAF6F1]/80 mb-8 font-light" data-id="element-112">
            Guía digital + Workbook de 14 días
          </p>

          <div className="flex items-baseline justify-center gap-2 mb-12" data-id="element-113">
            <span className="text-6xl font-serif font-bold text-[#B87333]" data-id="element-114">
              $6.39
            </span>
            <span className="text-[#FAF6F1]/60 text-lg uppercase tracking-wide" data-id="element-115">
              USD / Pago único
            </span>
          </div>

          <div className="max-w-md mx-auto space-y-6" data-id="element-116">
            <EditorialButton fullWidth onClick={() => {
            trackEvent('AddToCart', {
              content_name: 'Volví a mi cuerpo',
              content_type: 'product',
              value: 6.39,
              currency: 'USD'
            });
            window.open('https://mybloommind.com/cart/51675080556855:1', '_blank');
          }} data-id="element-117">
              Descargar ahora
            </EditorialButton>

            <div className="flex items-center justify-center gap-2 text-sm text-[#FAF6F1]/60" data-id="element-118">
              <Shield className="w-4 h-4" data-id="element-119" />
              <span data-id="element-120">Acceso inmediato. Lo abres hoy. Lo usas hoy.</span>
            </div>
          </div>
        </SectionWrapper>
      </div>

      {/* CLOSING */}
      <footer className="py-20 text-center px-6" data-id="element-121">
        <div className="max-w-2xl mx-auto" data-id="element-122">
          <p className="text-xl md:text-2xl text-[#4A3728] leading-relaxed font-serif italic mb-8" data-id="element-123">
            "No tienes que convertirte en alguien nueva para volver a sentir.
            Solo tienes que dejar de abandonarte para encajar."
          </p>
          <p className="text-lg font-medium text-[#B87333] tracking-wide uppercase" data-id="element-124">
            Vuelve a tu cuerpo antes de volver a alguien.
          </p>

          <div className="mt-16 pt-8 border-t border-[#E5D0C0] text-[#4A3728]/40 text-sm" data-id="element-125">
            © {new Date().getFullYear()} Volví a mi cuerpo. Todos los derechos
            reservados.
          </div>
        </div>
      </footer>
    </div>;
}